// Добавить человека в ведомость (тз, раздел 5.4.4: «поиск и добавление сотрудника»).
//
// ## Зачем это нужно на живой ведомости
//
// Ведомость собирается по назначениям на момент создания. Человека, вышедшего на объект пятого
// числа, в ней нет — а заплатить ему надо. До этой панели дверь на сервере была, хук в вебе
// был, а нажать было негде: единственным выходом оставалось пересоздать ведомость.
//
// ## Почему тариф не вводится
//
// Он снимается с НАЗНАЧЕНИЯ, и это не упрощение: понижение тарифа проводится отдельным
// действием с обязательным основанием. Поле ставки здесь позволило бы обойти основание, заведя
// строку с нужным числом, — и требование сценария 27 стало бы декоративным.
//
// ## Оклад — отдельный случай, а не «тариф без назначения»
//
// У административной ведомости назначений нет вовсе: там платят суммой. Поэтому у строки-оклада
// спрашивается сумма, а у часовой — назначение, и одно исключает другое.

import { useState } from 'react';

import { RiUserAddLine } from '@remixicon/react';

import type { ApiError } from '@/shared/errors/api-error';
import type { PayrollRowIn } from '@/shared/api/endpoints/payroll';
import { formatKopecks } from '@/shared/money/format';
import { parseRublesToKopecks } from '@/shared/money/parse';
import { useЛюдиДляВыбора } from '@/shared/people/lookup';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import * as Switch from '@/shared/ui/switch';
import { TextField } from '@/shared/ui/text-field';
import { formatDateOnly } from '@/shared/time/format';

import { useAddRow, useAssignmentsForPayroll, useSheetGrid } from '../queries';

export function AddPersonPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const сетка = useSheetGrid(id);
  const добавить = useAddRow(id);

  const [поиск, setПоиск] = useState('');
  const [человек, setЧеловек] = useState(NO_VALUE);
  const [назначение, setНазначение] = useState(NO_VALUE);
  const [оклад, setОклад] = useState(false);
  const [сумма, setСумма] = useState('');
  const [запрет, setЗапрет] = useState<string | null>(null);

  const люди = useЛюдиДляВыбора(поиск === '' ? undefined : поиск);
  const назначения = useAssignmentsForPayroll(человек === NO_VALUE ? null : человек);

  if (сетка.isPending) {
    return <Skeleton className='m-4 h-40' />;
  }

  // Ведомость не приехала — форму не рисуем вовсе: без неё неизвестно даже, объектовая она или
  // административная, а от этого зависит, что спрашивать. Пустая форма поверх неизвестности
  // предложила бы завести строку не туда.
  if (сетка.data?.payroll === undefined) {
    return (
      <div className='flex flex-col items-start gap-2 p-4'>
        <ErrorAlert error={сетка.error as never} />
        <Button.Root
          type='button'
          variant='neutral'
          mode='stroke'
          size='xsmall'
          onClick={() => void сетка.refetch()}
        >
          Повторить
        </Button.Root>
      </div>
    );
  }

  // Административная ведомость идёт без объекта, и назначений там не бывает: строка только
  // окладная. Признак берём из самой ведомости, а не из догадки по пустому списку назначений.
  const административная = сетка.data.payroll.site_id === null;
  const толькоОклад = оклад || административная;

  function отправить() {
    if (человек === NO_VALUE) {
      setЗапрет('Выберите человека: строка ведомости заводится на него.');
      return;
    }
    const тело: PayrollRowIn = { employee_id: человек };
    if (толькоОклад) {
      const разбор = parseRublesToKopecks(сумма);
      if (!разбор.ok || разбор.value <= 0) {
        setЗапрет('У строки-оклада сумма вводится прямо: часов и тарифа у неё нет.');
        return;
      }
      тело.row_type = 'salary';
      тело.accrued = разбор.value;
    } else {
      if (назначение === NO_VALUE) {
        setЗапрет(
          'Выберите назначение: тариф снимается с него, и без назначения начисление считать не от чего.',
        );
        return;
      }
      тело.assignment_id = назначение;
    }
    setЗапрет(null);
    добавить.mutate(тело, { onSuccess: onClose });
  }

  return (
    <div className='flex flex-col gap-3 p-4'>
      <p className='text-paragraph-xs text-text-sub-600'>
        {сетка.data.payroll.site_name ?? 'Аппарат управления'} · {сетка.data.payroll.status_title}
      </p>

      <TextField
        id='add-person-search'
        label='Поиск по фамилии'
        value={поиск}
        onChange={(значение) => {
          setПоиск(значение);
          setЧеловек(NO_VALUE);
          setНазначение(NO_VALUE);
        }}
        placeholder='начните вводить фамилию'
        hint='Список сужается по мере ввода. Человек может быть и не назначен на этот объект.'
      />

      <SelectField
        id='add-person-employee'
        label='Человек'
        required
        value={человек}
        onChange={(значение) => {
          setЧеловек(значение);
          setНазначение(NO_VALUE);
        }}
        options={(люди.data?.items ?? []).map((строка) => ({
          value: строка.id,
          label: строка.display_name,
        }))}
        placeholder='выберите человека'
        pending={люди.isPending}
        failed={люди.isError}
        failedError={люди.error as ApiError | null}
        emptyNote='По этой фамилии никого нет. Проверьте написание либо заведите человека в разделе «Люди».'
      />

      {!административная && (
        <label className='flex items-center gap-2 text-paragraph-sm text-text-sub-600'>
          <Switch.Root checked={оклад} onCheckedChange={setОклад} />
          Строка-оклад: сумма без часов и тарифа
        </label>
      )}

      {толькоОклад ? (
        <TextField
          id='add-person-amount'
          label='Сумма начисления'
          required
          value={сумма}
          onChange={setСумма}
          placeholder='30 000'
          inputMode='decimal'
          hint={
            административная
              ? 'У ведомости аппарата управления объекта нет, значит нет и назначений: платят суммой.'
              : 'Оклад не делится на часы: сумма идёт в начисление как есть.'
          }
        />
      ) : (
        <SelectField
          id='add-person-assignment'
          label='Назначение'
          required
          value={назначение}
          onChange={setНазначение}
          options={(назначения.data?.items ?? []).map((строка) => ({
            value: строка.id,
            label: `${строка.site_name} · ${строка.post_name} · с ${formatDateOnly(строка.date_from)}${
              строка.period_amount === null
                ? ''
                : ` · ${formatKopecks(строка.period_amount)} за период`
            }`,
          }))}
          placeholder={человек === NO_VALUE ? 'сначала выберите человека' : 'выберите назначение'}
          pending={человек !== NO_VALUE && назначения.isPending}
          failed={назначения.isError}
          failedError={назначения.error as ApiError | null}
          // Пока человек не выбран, «у человека нет назначений» — неправда: человека нет.
          emptyNote={
            человек === NO_VALUE
              ? undefined
              : 'У человека нет ни одного назначения. Поставьте его на объект в разделе «Люди» либо заведите строку-оклад.'
          }
          hint='Тариф снимается с назначения. Понижают его отдельным действием, с основанием.'
        />
      )}

      {запрет !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiUserAddLine} />
          {запрет}
        </Hint.Root>
      )}
      {добавить.isError && <ErrorAlert error={добавить.error as never} />}

      <div className='flex items-center gap-2'>
        <Button.Root type='button' variant='primary' mode='filled' size='small' onClick={отправить}>
          {добавить.isPending ? 'Добавляем…' : 'Добавить в ведомость'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </div>
  );
}
